import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { getServiceLine } from '../data/services';
import { ARTICLES } from '../data/insights';
import { Seo } from '../components/Seo';
import { serviceSchema, breadcrumbSchema } from '../lib/seo';
import { NotFound } from './NotFound';
import { Photo } from '../components/ui/Photo';
import { PHOTOS, type SitePhoto } from '../data/photos';
const LINE_PHOTOS: Record<string, SitePhoto> = {
  intelligence: PHOTOS.officeWide,
  growth: PHOTOS.planIndonesia,
  ai: PHOTOS.workshopTable,
  gov: PHOTOS.govTraining
};
export function ServiceLinePage() {
  const {
    lineId
  } = useParams<{
    lineId: string;
  }>();
  const line = getServiceLine(lineId ?? '');
  if (!line) return <NotFound data-id="element-3381" data-mp="3jhmvvydrgr8r" />;
  const related = ARTICLES.filter(article => article.serviceLine === line.id).slice(0, 3);
  return <>
      <Seo title={`${line.name}: ${line.short.toLowerCase()}`} description={`${line.promise} ${line.outcome}`} path={`/services/${line.id}`} jsonLd={[serviceSchema({
      name: line.name,
      description: `${line.promise} ${line.outcome}`,
      path: `/services/${line.id}`,
      serviceType: line.short,
      audience: line.who,
      offers: line.packages.map(pkg => pkg.name)
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }, {
      name: line.name,
      path: `/services/${line.id}`
    }])]} data-id="element-3382" data-mp="2kgfkiqzsssqx" />

      <section className="surface-dark" data-id="element-3383" data-mp="1wqbrfdk8kbe6">
        <Container className="py-14 sm:py-20" data-id="element-3384" data-mp="3m36wryeax73o">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-3385" data-mp="3m1c7k7vzysts">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-3386" data-mp="3r1gtidfocfre">
              <li data-id="element-3387" data-mp="3plmh2uxmpky9"><Link to="/" className="hover:text-white" data-id="element-3388" data-mp="3plmfiqhzdcw5">Home</Link></li>
              <li aria-hidden="true" data-id="element-3389" data-mp="3kmjcfblk9gqt"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3390" data-mp="3km032j9z80oe" /></li>
              <li data-id="element-3391" data-mp="3kom8ac9ea43g"><Link to="/services" className="hover:text-white" data-id="element-3392" data-mp="3fn6qct2ikdpa">Services</Link></li>
              <li aria-hidden="true" data-id="element-3393" data-mp="3fp9gre7kw8uj"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-3394" data-mp="3bluwgbclx6ba" /></li>
              <li aria-current="page" className="text-white/80" data-id="element-3395" data-mp="3bnebxzqfvdc0">{line.name}</li>
            </ol>
          </nav>
          <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-center" data-id="element-3396" data-mp="c8kei4f13lao">
            <div data-id="element-3397" data-mp="78551nxvaxxz">
              <Kicker data-id="element-3398" data-mp="79pp2jmk2yhl">{line.short}</Kicker>
              <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-3399" data-mp="2fv6xkmlmrkm">
                {line.name}
              </h1>
              <p className="mt-6 max-w-2xl text-[19px] text-white/80" data-id="element-3400" data-mp="3uimmr4qkglou">{line.promise}</p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-3401" data-mp="uz28sdfieoy5">
                <Button to="/contact" size="lg" data-id="element-3402" data-mp="q5ft0ce1u4y4">Book a discovery call</Button>
                <Button to="/augmented-intelligence" variant="reversed" size="lg" data-id="element-3403" data-mp="28tr3eo06s0uv">Read our method</Button>
              </div>
            </div>
            <Photo photo={LINE_PHOTOS[line.id] ?? PHOTOS.planIndonesia} aspect="aspect-[4/3]" tone="dark" eager data-id="element-3404" data-mp="2dsc2ul7m6gi9" />
          </div>
        </Container>
      </section>

      <Section data-id="element-3405" data-mp="2mttfftltc027">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-3406" data-mp="2mublfhya1snb">
          <div data-id="element-3407" data-mp="2rs3a833mkyna">
            <Kicker data-id="element-3408" data-mp="2rsmvntshi562">Who it is for</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-3409" data-mp="2rrl9at60pw91">{line.who}</p>
          </div>
          <div data-id="element-3410" data-mp="2wpda6con4spd">
            <Kicker data-id="element-3411" data-mp="2wp3fibsnhxdc">The outcome</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-3412" data-mp="31hwjuyk7j3ow">{line.outcome}</p>
          </div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="sub-services" data-id="element-3413" data-mp="35jhwcistxfyk">
        <SectionHeader id="sub-services" kicker="What we do" title="Four areas of work" data-id="element-3414" data-mp="35l2q467bkuvd" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-3415" data-mp="3ag8dl9gdehlk">
          {line.subServices.map((sub, index) => <Reveal key={sub.name} delay={index * 0.05} data-id="element-3416" data-mp="31up76fsojhjb" data-mpi={index}>
              <Card className="h-full" data-id="element-3417" data-mp="31te129brhxp1">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-3418" data-mp="2wvm0ys0yr59r">{sub.name}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-3419" data-mp="2wtro80zuijgr">{sub.detail}</p>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section aria-labelledby="use-cases" data-id="element-3420" data-mp="36mqfxrqn2q4l">
        <SectionHeader id="use-cases" kicker="In practice" title="Examples by sector" lead="Anonymised until each client agrees to be named. The numbers are theirs, not ours." data-id="element-3421" data-mp="36nru452ybsqj" />
        <div className="mt-10 divide-y divide-line border-y border-line" data-id="element-3422" data-mp="2suk066reel5o">
          {line.useCases.map(useCase => <div key={useCase.sector} className="grid gap-3 py-6 sm:grid-cols-[180px_1fr]" data-id="element-3423" data-mp="2ntw7zhrfny9v">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-3424" data-mp="82qjdhccwafc">{useCase.sector}</p>
              <p className="text-[16px] text-body" data-id="element-3425" data-mp="33x5vox2kjf6">{useCase.outcome}</p>
            </div>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="packages" data-id="element-3426" data-mp="aqar91pa8i5g">
        <SectionHeader id="packages" kicker="Engagements" title={`Working with ${line.name}`} lead="Three ways this line is usually scoped. Every engagement begins with a discovery call and a written proposal, agreed before any work starts." data-id="element-3427" data-mp="focbvr2cwpj1" />
        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-3428" data-mp="tfqsjr8skemx">
          {line.packages.map(option => <Card key={option.name} className="flex h-full flex-col" data-id="element-3429" data-mp="ycqze4dgtlf3">
              <h3 className="font-display text-[22px] text-ink" data-id="element-3430" data-mp="kmnlq1snj019">{option.name}</h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-3431" data-mp="onzbc7bizl9g">{option.summary}</p>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3432" data-mp="om600epywcv4">
                What it includes
              </p>
              <ul className="mt-4 space-y-3 text-[14px] text-body" data-id="element-3433" data-mp="1h4jrzxomh4ma">
                {option.includes.map(item => <li key={item} className="flex gap-3" data-id="element-3434" data-mp="1qyz4ar958vu3">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-3435" data-mp="1myye69comw77" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto pt-7" data-id="element-3436" data-mp="1i1f2nh7dcxiy">
                <Button to="/contact" variant="secondary" className="w-full" data-id="element-3437" data-mp="1czfwjzk8vmib">
                  Discuss this engagement
                </Button>
              </div>
            </Card>)}
        </div>
        <Callout className="mt-8" data-id="element-3438" data-mp="139mupq9wmlr9">
          Scope, timing and fees are set out in a written proposal after a discovery call. Nothing on this page is
          financial or legal advice, and Cavalla Solution is not a financial adviser.
        </Callout>
      </Section>

      {related.length > 0 && <Section aria-labelledby="related" data-id="element-3439" data-mp="p9llnnjhk80p">
          <SectionHeader id="related" kicker="Related reading" title={`Insights on ${line.short}`} data-id="element-3440" data-mp="pcrdvbnevq0n" />
          <div className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-3441" data-mp="a7wyjomec7af">
            {related.map(article => <article key={article.slug} data-id="element-3442" data-mp="f6zqc7dd8h0g">
                <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-3443" data-mp="f5oowe7e7lci">{article.sectors[0]}</p>
                <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-3444" data-mp="k7e0buyj2114">
                  <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-3445" data-mp="p44geiac0wmj">
                    {article.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-3446" data-mp="3slct6nq7d6ae">{article.excerpt}</p>
              </article>)}
          </div>
        </Section>}
    </>;
}